"use client";

import { useEffect, useMemo, useState, type ElementType } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  ChevronDown,
  ChevronRight,
  LogOut,
  Moon,
  PanelLeftClose,
  Search,
  Sun,
  User,
  XCircle,
  Loader2,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { useAuthStore } from "@/store/auth-store";
import { useActiveClientStore } from "@/store/active-client-store";
import { useTheme } from "@/hooks/use-theme";
import { apiClient } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { branding } from "@/config/branding";
import {
  baseNavigation,
  adminNavigation,
  adminGlobalNavigation,
  isNavigationGroup,
  NavigationItem,
  NavigationGroup,
} from "@/config/navigation";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UserRole } from "@/types/user-role";
import type { Client } from "@/types/client";
import type { Vehicle } from "@/types/vehicle";

interface AppSidebarProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
  isDesktop: boolean;
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

type CompanyBranding = {
  name: string;
  logo: string | null;
};

const openGroupStorageKey = "garage-sidebar-open-group";

const readOpenGroup = () => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.sessionStorage.getItem(openGroupStorageKey);
  } catch {
    return null;
  }
};

const persistOpenGroup = (value: string | null) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    if (!value) {
      window.sessionStorage.removeItem(openGroupStorageKey);
      return;
    }
    window.sessionStorage.setItem(openGroupStorageKey, value);
  } catch {
    /* ignore persistence errors */
  }
};

export function AppSidebar({
  isSidebarOpen,
  setIsSidebarOpen,
  isDesktop,
  isCollapsed,
  setIsCollapsed,
}: AppSidebarProps) {
  const pathname = usePathname();
  const router = useRouter();

  const { user } = useAuthStore();
  const { theme, toggleTheme } = useTheme();
  const {
    client: activeClient,
    vehicle: activeVehicle,
    setActiveClient,
    setActiveVehicle,
    clearActiveVehicle,
    clearActiveClient,
  } = useActiveClientStore();

  const [clientSearch, setClientSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showClientResults, setShowClientResults] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [showVehicleResults, setShowVehicleResults] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(readOpenGroup);
  const isSidebarCollapsed = isDesktop && isCollapsed;

  const permissionKeys = useMemo(
    () => user?.permissions?.map((p) => p.permission) ?? [],
    [user?.permissions],
  );
  const isSuperAdmin = user?.role === UserRole.SuperAdmin;
  const isTenantAdmin = user?.role === UserRole.TenantAdmin;
  const hasBackupPermission = permissionKeys.includes("backup.manage");

  const navItems = useMemo<NavigationItem[]>(() => {
    const items: NavigationItem[] = [...baseNavigation];
    if (isTenantAdmin || isSuperAdmin) {
      items.push(adminNavigation);
    }
    if (isSuperAdmin || hasBackupPermission) {
      const children = isSuperAdmin
        ? adminGlobalNavigation.children
        : adminGlobalNavigation.children.filter(
            (item) => item.href === "/admin-global/backup",
          );

      if (children.length) {
        items.push({ ...adminGlobalNavigation, children });
      }
    }
    return items;
  }, [hasBackupPermission, isSuperAdmin, isTenantAdmin]);

  const resolvedOpenGroup = useMemo(() => {
    const groupLabels = navItems
      .filter((item): item is NavigationGroup => isNavigationGroup(item))
      .map((item) => item.label);

    if (openGroup && groupLabels.includes(openGroup)) {
      return openGroup;
    }

    if (!pathname) {
      return null;
    }

    const activeGroup = navItems.find(
      (item) =>
        isNavigationGroup(item) &&
        item.children.some((child) => pathname.startsWith(child.href)),
    );
    return activeGroup && isNavigationGroup(activeGroup)
      ? activeGroup.label
      : null;
  }, [navItems, openGroup, pathname]);

  useEffect(() => {
    persistOpenGroup(openGroup);
  }, [openGroup]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(clientSearch.trim());
    }, 350);
    return () => clearTimeout(timeout);
  }, [clientSearch]);

  useEffect(() => {
    if (!activeVehicle) {
      return;
    }
    if (!activeClient || activeVehicle.client?.id !== activeClient.id) {
      clearActiveVehicle();
    }
  }, [activeClient, activeVehicle, clearActiveVehicle]);

  const clientsQuery = useQuery({
    queryKey: ["sidebar-clients", debouncedSearch],
    enabled: debouncedSearch.length > 1,
    queryFn: async ({ queryKey }) => {
      const term = queryKey[1] as string;
      const params = new URLSearchParams({
        search: term,
        includeVehicles: "false",
      });
      const url = `/clientes?${params.toString()}`;
      const response = await apiClient.get<Client[]>(url);
      return response.data;
    },
  });

  const vehiclesQuery = useQuery({
    queryKey: ["sidebar-vehicles", activeClient?.id ?? null],
    enabled: Boolean(activeClient?.id),
    queryFn: async ({ queryKey }) => {
      const clientId = queryKey[1] as string;
      const response = await apiClient.get<Vehicle[]>(
        `/veiculos?clientId=${encodeURIComponent(clientId)}`,
      );
      return response.data;
    },
  });

  const companyBrandingQuery = useQuery({
    queryKey: ["company-branding", user?.company?.id ?? null],
    enabled: Boolean(user?.company?.id),
    queryFn: async () => {
      const response = await apiClient.get<CompanyBranding>("/branding");
      return response.data;
    },
  });

  const resolvedBrandLogo =
    companyBrandingQuery.data?.logo?.trim() || branding.logo;
  const resolvedBrandName =
    companyBrandingQuery.data?.name?.trim() || branding.name;

  const filteredVehicles = useMemo(() => {
    const vehicles = vehiclesQuery.data ?? [];
    const term = vehicleSearch.trim().toLowerCase();
    if (!term) {
      return vehicles;
    }
    return vehicles.filter((vehicle) => {
      const target =
        `${vehicle.plate} ${vehicle.brand} ${vehicle.model} ${vehicle.year}`.toLowerCase();
      return target.includes(term);
    });
  }, [vehicleSearch, vehiclesQuery.data]);

  const handleLogout = () => {
    void apiClient
      .post("/auth/logout", {})
      .catch(() => null)
      .finally(() => {
        useAuthStore.getState().clearAuth();
        router.replace("/login");
      });
  };

  const toggleCollapse = () => {
    if (!isDesktop) return;
    setIsCollapsed(!isCollapsed);
  };

  const handleMobileClose = () => {
    if (!isDesktop) setIsSidebarOpen(false);
  };

  const handleSelectClient = (client: Client) => {
    setActiveClient(client);
    setClientSearch("");
    setShowClientResults(false);
    setVehicleSearch("");
    setShowVehicleResults(false);
  };

  const handleSelectVehicle = (vehicle: Vehicle) => {
    setActiveVehicle(vehicle);
    setVehicleSearch("");
    setShowVehicleResults(false);
  };

  const renderIcon = (Icon?: ElementType, className?: string) => {
    if (!Icon) return null;
    return <Icon className={cn("h-[15px] w-[15px] shrink-0", className)} strokeWidth={1.75} />;
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-30 flex h-screen flex-col overflow-hidden border-r transition-all duration-300",
        "bg-[var(--surface)] border-[var(--border)]",
        !isDesktop && "bottom-[60px] h-[calc(100dvh-60px)] pb-[env(safe-area-inset-bottom)]",
        isSidebarCollapsed ? "w-14" : "w-screen max-w-xs sm:max-w-sm lg:w-64",
      )}
      style={{
        transform: isSidebarOpen ? "translateX(0)" : "translateX(-110%)",
        opacity: isSidebarOpen ? 1 : 0,
        pointerEvents: isSidebarOpen ? undefined : "none",
      }}
    >
      {/* HEADER */}
      <div
        className={cn(
          "flex shrink-0 items-center border-b border-[var(--border)]",
          isSidebarCollapsed
            ? "h-14 justify-center"
            : "min-h-[64px] px-4 py-3",
        )}
        style={{ background: "var(--surface)" }}
      >
        {!isSidebarCollapsed && (
          <div className="flex flex-1 items-center overflow-hidden">
            {resolvedBrandLogo ? (
              <Image
                src={resolvedBrandLogo}
                alt={`Logo ${resolvedBrandName}`}
                width={160}
                height={38}
                unoptimized
                className="h-[38px] w-auto max-w-[160px] object-contain"
              />
            ) : (
              <span className="truncate text-[15px] font-bold text-[var(--primary)]">
                {resolvedBrandName}
              </span>
            )}
          </div>
        )}

        <button
          onClick={isDesktop ? toggleCollapse : handleMobileClose}
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-[5px] text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-muted)] hover:text-[var(--foreground)]",
            !isSidebarCollapsed && "ml-2",
          )}
          title={
            isDesktop
              ? isSidebarCollapsed
                ? "Expandir menu"
                : "Recolher menu"
              : "Fechar menu"
          }
        >
          {isDesktop ? (
            isSidebarCollapsed ? (
              <ChevronRight size={16} />
            ) : (
              <PanelLeftClose size={16} />
            )
          ) : (
            <XCircle size={18} />
          )}
        </button>
      </div>

      {/* CLIENT CONTEXT (Expanded Only) */}
      {!isSidebarCollapsed && (
        <div className="shrink-0 border-b border-[var(--border)] px-[10px] pb-3 pt-2">
          <div className="mb-1.5 flex items-center justify-between">
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.06em]"
              style={{ color: "var(--text-muted)" }}
            >
              Contexto
            </span>
            {(activeClient || activeVehicle) && (
              <button
                onClick={clearActiveClient}
                className="text-[10px] font-semibold text-[var(--error)] hover:underline"
              >
                Limpar
              </button>
            )}
          </div>

          {/* Busca de cliente */}
          <div className="relative">
            <div
              className={cn(
                "flex items-center gap-2 rounded-[7px] border px-[10px] py-[6px] text-[13px] transition-colors",
                "border-[var(--border)] bg-[var(--surface-muted)]",
                "focus-within:border-[var(--primary)] focus-within:bg-[var(--surface)]",
              )}
            >
              <Search className="h-3.5 w-3.5 shrink-0 text-[var(--text-muted)]" />
              <input
                value={activeClient ? activeClient.name : clientSearch}
                onChange={(e) => {
                  if (activeClient) clearActiveClient();
                  setClientSearch(e.target.value);
                  setShowClientResults(true);
                  setVehicleSearch("");
                  setShowVehicleResults(false);
                }}
                onFocus={() => setShowClientResults(true)}
                placeholder={
                  activeClient ? "Cliente selecionado" : "Buscar cliente..."
                }
                className={cn(
                  "w-full bg-transparent text-[13px] outline-none placeholder:text-[var(--text-muted)]",
                  activeClient && "font-medium text-[var(--primary)]",
                )}
              />
              {clientsQuery.isFetching && (
                <Loader2 className="h-3 w-3 animate-spin text-[var(--text-muted)]" />
              )}
            </div>

            {showClientResults && debouncedSearch.length > 1 && !activeClient && (
              <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-52 overflow-y-auto rounded-[7px] border border-[var(--border)] bg-[var(--surface)] p-1 shadow-[0_4px_12px_rgba(0,0,0,0.10)]">
                {clientsQuery.data?.map((client) => (
                  <button
                    key={client.id}
                    onClick={() => handleSelectClient(client)}
                    className="flex w-full flex-col rounded-[5px] px-3 py-2 text-left text-[13px] hover:bg-[var(--surface-muted)]"
                  >
                    <span className="font-medium text-[var(--foreground)]">
                      {client.name}
                    </span>
                    <span className="text-[11px] text-[var(--muted-foreground)]">
                      {formatClientId(client.document)}
                    </span>
                  </button>
                ))}
                {!clientsQuery.isFetching && !clientsQuery.data?.length && (
                  <p className="px-3 py-2 text-[12px] text-[var(--muted-foreground)]">
                    Nenhum cliente encontrado.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Busca de veiculo */}
          <div className="relative mt-2">
            <div
              className={cn(
                "flex items-center gap-2 rounded-[7px] border px-[10px] py-[6px] text-[13px] transition-colors",
                "border-[var(--border)] bg-[var(--surface-muted)]",
                "focus-within:border-[var(--primary)] focus-within:bg-[var(--surface)]",
              )}
            >
              <Search className="h-3.5 w-3.5 shrink-0 text-[var(--text-muted)]" />
              <input
                value={
                  activeVehicle
                    ? formatVehicleContextLabel(activeVehicle)
                    : vehicleSearch
                }
                onChange={(e) => {
                  if (activeVehicle) {
                    clearActiveVehicle();
                  }
                  setVehicleSearch(e.target.value);
                  setShowVehicleResults(true);
                }}
                onFocus={() => setShowVehicleResults(true)}
                placeholder={
                  activeClient
                    ? activeVehicle
                      ? "Veículo selecionado"
                      : "Buscar veículo..."
                    : "Selecione um cliente primeiro"
                }
                disabled={!activeClient}
                className={cn(
                  "w-full bg-transparent text-[13px] outline-none placeholder:text-[var(--text-muted)] disabled:cursor-not-allowed disabled:opacity-60",
                  activeVehicle && "font-medium text-[var(--primary)]",
                )}
              />
              {vehiclesQuery.isFetching && activeClient && (
                <Loader2 className="h-3 w-3 animate-spin text-[var(--text-muted)]" />
              )}
            </div>

            {showVehicleResults && activeClient && !activeVehicle && (
              <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-52 overflow-y-auto rounded-[7px] border border-[var(--border)] bg-[var(--surface)] p-1 shadow-[0_4px_12px_rgba(0,0,0,0.10)]">
                {filteredVehicles.map((vehicle) => (
                  <button
                    key={vehicle.id}
                    onClick={() => handleSelectVehicle(vehicle)}
                    className="flex w-full flex-col rounded-[5px] px-3 py-2 text-left text-[13px] hover:bg-[var(--surface-muted)]"
                  >
                    <span className="font-medium text-[var(--foreground)]">
                      {formatVehiclePlate(vehicle.plate)} - {vehicle.brand}{" "}
                      {vehicle.model}
                    </span>
                    <span className="text-[11px] text-[var(--muted-foreground)]">
                      Ano {vehicle.year}
                    </span>
                  </button>
                ))}
                {!vehiclesQuery.isFetching && !filteredVehicles.length && (
                  <p className="px-3 py-2 text-[12px] text-[var(--muted-foreground)]">
                    {vehicleSearch.trim()
                      ? "Nenhum veículo encontrado."
                      : "Nenhum veículo encontrado para o cliente ativo."}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* NAVIGATION ITEMS */}
      <div className="flex-1 overflow-y-auto px-[6px] py-2">
        <nav>
          <TooltipProvider delayDuration={0}>
            {navItems.map((item, index) => {
              if (isNavigationGroup(item)) {
                const isActiveGroup = item.children.some((child) =>
                  pathname?.startsWith(child.href),
                );
                const isOpen =
                  resolvedOpenGroup === item.label || isActiveGroup;

                if (isSidebarCollapsed) {
                  return (
                    <NavigationTooltip
                      key={index}
                      label={item.label}
                      side="right"
                    >
                      <button
                        className={cn(
                          "my-[1px] flex w-full justify-center rounded-[6px] p-2.5 transition-colors",
                          isActiveGroup
                            ? "bg-[#1e2736] text-white"
                            : "text-[var(--text-muted)] hover:bg-[var(--surface-muted)] hover:text-[var(--foreground)]",
                        )}
                      >
                        {renderIcon(item.icon)}
                      </button>
                    </NavigationTooltip>
                  );
                }

                return (
                  <div key={index} className="mb-3">
                    <div
                      className="mb-1 flex cursor-pointer items-center justify-between px-[10px] py-1"
                      onClick={() =>
                        setOpenGroup(
                          resolvedOpenGroup === item.label ? null : item.label,
                        )
                      }
                    >
                      <span
                        className="text-[10.5px] font-semibold uppercase tracking-[0.06em]"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {item.label}
                      </span>
                      <ChevronDown
                        className={cn(
                          "h-3 w-3 transition-transform",
                          isOpen && "rotate-180",
                        )}
                        style={{ color: "var(--text-muted)" }}
                      />
                    </div>

                    {isOpen && (
                      <div className="space-y-[1px]">
                        {item.children.map((child) => {
                          const isActive = pathname?.startsWith(child.href);
                          return (
                            <Link
                              key={child.href}
                              href={child.href}
                              onClick={handleMobileClose}
                              className={cn(
                                "mx-[6px] flex items-center gap-[9px] rounded-[6px] px-[10px] py-[7px] text-[13px] transition-colors",
                                isActive
                                  ? "bg-[#1e2736] font-medium text-white"
                                  : "text-[var(--muted-foreground)] hover:bg-[var(--surface-muted)] hover:text-[var(--foreground)]",
                              )}
                            >
                              {renderIcon(
                                child.icon,
                                isActive
                                  ? "text-white"
                                  : "text-[var(--text-muted)]",
                              )}
                              <span>{child.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              } else {
                const isActive =
                  item.href === "/"
                    ? pathname === "/"
                    : pathname?.startsWith(item.href);

                if (isSidebarCollapsed) {
                  return (
                    <NavigationTooltip
                      key={index}
                      label={item.label}
                      side="right"
                    >
                      <Link
                        href={item.href}
                        className={cn(
                          "my-[1px] flex w-full justify-center rounded-[6px] p-2.5 transition-colors",
                          isActive
                            ? "bg-[#1e2736] text-white"
                            : "text-[var(--text-muted)] hover:bg-[var(--surface-muted)] hover:text-[var(--foreground)]",
                        )}
                      >
                        {renderIcon(item.icon)}
                      </Link>
                    </NavigationTooltip>
                  );
                }

                return (
                  <Link
                    key={index}
                    href={item.href}
                    onClick={handleMobileClose}
                    className={cn(
                      "my-[1px] mx-[6px] flex items-center gap-[9px] rounded-[6px] px-[10px] py-[7px] text-[13px] font-medium transition-colors",
                      isActive
                        ? "bg-[#1e2736] text-white"
                        : "text-[var(--muted-foreground)] hover:bg-[var(--surface-muted)] hover:text-[var(--foreground)]",
                    )}
                  >
                    {renderIcon(
                      item.icon,
                      isActive
                        ? "text-white"
                        : "text-[var(--text-muted)]",
                    )}
                    <span>{item.label}</span>
                  </Link>
                );
              }
            })}
          </TooltipProvider>
        </nav>
      </div>

      {/* FOOTER */}
      <div
        className={cn(
          "shrink-0 border-t border-[var(--border)] bg-[var(--surface)]",
          isSidebarCollapsed ? "p-2" : "p-3",
        )}
      >
        {isSidebarCollapsed ? (
          <div className="flex flex-col items-center gap-1">
            <button
              onClick={toggleTheme}
              className="flex h-[30px] w-[30px] items-center justify-center rounded-[6px] text-[var(--muted-foreground)] transition-colors hover:bg-[var(--surface-muted)]"
            >
              {theme === "dark" ? <Moon size={15} /> : <Sun size={15} />}
            </button>
            <button
              onClick={handleLogout}
              className="flex h-[30px] w-[30px] items-center justify-center rounded-[6px] text-[var(--error)] transition-colors hover:bg-[var(--surface-muted)]"
            >
              <LogOut size={15} />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--surface-muted)] text-[var(--foreground)]">
                <User size={14} />
              </div>
              <div className="min-w-0 overflow-hidden">
                <p className="truncate text-[12.5px] font-medium text-[var(--foreground)]">
                  {user?.fullName}
                </p>
                <p className="truncate text-[11px] capitalize text-[var(--muted-foreground)]">
                  {user?.role}
                </p>
              </div>
            </div>

            <div className="flex shrink-0 gap-1">
              <TooltipProvider>
                <NavigationTooltip label="Alternar tema">
                  <button
                    onClick={toggleTheme}
                    className="flex h-[30px] w-[30px] items-center justify-center rounded-[6px] text-[var(--muted-foreground)] transition-colors hover:bg-[var(--surface-muted)]"
                  >
                    {theme === "dark" ? <Moon size={15} /> : <Sun size={15} />}
                  </button>
                </NavigationTooltip>
                <NavigationTooltip label="Sair">
                  <button
                    onClick={handleLogout}
                    className="flex h-[30px] w-[30px] items-center justify-center rounded-[6px] text-[var(--error)] transition-colors hover:bg-[var(--surface-muted)]"
                  >
                    <LogOut size={15} />
                  </button>
                </NavigationTooltip>
              </TooltipProvider>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

function NavigationTooltip({
  children,
  label,
  side = "top",
}: {
  children: React.ReactNode;
  label: string;
  side?: "top" | "right";
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} className="text-[13px] font-medium">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function formatClientId(doc?: string) {
  if (!doc) return "";
  return doc.length > 11
    ? doc.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5")
    : doc.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

function formatVehiclePlate(plate?: string) {
  const normalized = (plate ?? "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  if (normalized.length !== 7) {
    return plate ?? "";
  }
  return `${normalized.slice(0, 3)}-${normalized.slice(3)}`;
}

function formatVehicleContextLabel(vehicle: Vehicle) {
  return `${formatVehiclePlate(vehicle.plate)} - ${vehicle.brand} ${vehicle.model}`;
}
